import HorizontalDivider from '@/global/components/Divider/HorizontalDivider'
import IconeAjuda from '@/global/components/IconeAjuda'
import Grid from '@/global/components/ui/Grid'
import {
    FormDatePicker,
    FormField,
    FormFieldCheckbox,
    FormFieldSelect,
} from 'global/components/Form'
import { Link } from 'global/components/Link'
import { hasPermission } from 'hooks/useBackendMenu'
import { ExternalLinkIcon } from 'lucide-react'
import { useFormContext } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { regrasCalculo, tabelaHorarios } from 'service/api'
import { useStore } from 'store'
import AutocompleteFuncao from './AutocompleteFuncao'

/** Valor sentinela da opção que leva ao cadastro de um novo registro. */
const ADICIONAR_NOVA = '0'

/**
 * Maior matrícula aceita (dez dígitos).
 */
export const MATRICULA_MAXIMA = 9_999_999_999

export function ParametrosDoCalculo({ editar }: { editar?: boolean }) {
    const { empresaId, id } = useParams()
    const cliente = useStore(state => state.customer)
    const empresaIdStore = useStore(state => state.companyId)
    const navigate = useNavigate()
    const { watch } = useFormContext()
    const tabelaHorarioId = watch('tabelaHorarioId')

    /**
     * A opção "Adicionar Nova" não é um valor do formulário: guarda o rascunho
     * do funcionário e navega para o cadastro correspondente.
     */
    const adicionarNova = (href: string) => {
        localStorage.setItem('@rascunho:funcionario', JSON.stringify(watch()))
        navigate(href)
    }

    const opcaoAdicionarNova = (permitido: boolean) =>
        permitido ? [{ value: ADICIONAR_NOVA, label: 'Adicionar Nova' }] : []

    const { data: tabelaHorariosData } = tabelaHorarios.useFetch({
        queryParams: id ? { clienteId: id } : {},
        enabled: !!id || !!cliente,
    })

    const { data: rgCalculoData } = regrasCalculo.useFetch({
        queryParams: id ? { clienteId: id } : {},
        enabled: !!empresaId || !!empresaIdStore,
    })

    return (
        <section id="parametros">
            <HorizontalDivider className="my-4">
                Parâmetros do cálculo
            </HorizontalDivider>
            <Grid container columns={24} className="items-start">
                <Grid item xs={8}>
                    <FormField
                        name="matricula"
                        label="Matrícula"
                        max={MATRICULA_MAXIMA}
                        placeholder={
                            editar ? undefined : '(Gerar automaticamente)'
                        }
                        required={editar}
                        type="number"
                        className="[&_input::-webkit-outer-spin-button]:appearance-none [&_input::-webkit-outer-spin-button]:m-0 [&_input::-webkit-inner-spin-button]:appearance-none [&_input::-webkit-inner-spin-button]:m-0 [&_input[type=number]]:[-moz-appearance:textfield]"
                    />
                </Grid>
                <Grid item xs={8}>
                    <FormDatePicker
                        name="dtAdmissao"
                        label="Data de admissão"
                        type="date"
                        required
                    />
                </Grid>
                <Grid item xs={8}>
                    <FormDatePicker
                        name="dtRescisao"
                        label="Data de rescisão"
                        type="date"
                    />
                </Grid>
                <Grid item xs={12}>
                    <div onKeyDownCapture={e => e.stopPropagation()}>
                        <FormFieldSelect
                            required
                            name="regraCalculo"
                            label="Regra de cálculo"
                            options={[
                                ...opcaoAdicionarNova(
                                    hasPermission([
                                        'create',
                                        'RegrasDeCálculo',
                                    ]),
                                ),
                                ...(rgCalculoData?.map(regra => ({
                                    value: +regra.id,
                                    label: regra.nome,
                                })) ?? []),
                            ]}
                            onChange={valor => {
                                if (valor === ADICIONAR_NOVA) {
                                    adicionarNova(
                                        '/cadastros/regras-calculo/adicionar',
                                    )
                                }
                            }}
                        />
                    </div>
                </Grid>
                <Grid item xs={12}>
                    <div onKeyDownCapture={e => e.stopPropagation()}>
                        <FormFieldSelect
                            name="tabelaHorarioId"
                            label={'Tabela de horário'}
                            options={[
                                ...opcaoAdicionarNova(
                                    hasPermission([
                                        'create',
                                        'TabelaDeHorários',
                                    ]),
                                ),
                                { value: '', label: 'Nenhuma (Horista)' },
                                ...(tabelaHorariosData?.map(tabelaHorario => ({
                                    value: +tabelaHorario.id,
                                    label: tabelaHorario.nome,
                                })) ?? []),
                            ]}
                            onChange={valor => {
                                if (valor === ADICIONAR_NOVA) {
                                    adicionarNova(
                                        '/cadastros/tabela-horarios/adicionar',
                                    )
                                }
                            }}
                            {...(editar && {
                                disabled: true,
                                label: tabelaHorarioId ? (
                                    <Link
                                        href={`/cadastros/tabela-horarios/${tabelaHorarioId}/editar`}
                                        className="inline-flex items-center gap-2"
                                    >
                                        Tabela de horário atual{' '}
                                        <ExternalLinkIcon className="size-4" />
                                    </Link>
                                ) : (
                                    'Tabela de horário'
                                ),
                            })}
                        />
                    </div>
                </Grid>
                <Grid item xs={24}>
                    <AutocompleteFuncao />
                </Grid>
                <Grid item xs={12}>
                    <FormFieldCheckbox
                        name="horarioNoturno"
                        label="Horário noturno"
                        endAdornment={
                            <IconeAjuda>
                                Marque quando a jornada do funcionário avança
                                sobre a madrugada.
                                <br />
                                <br />O cálculo passa a tratar as marcações após
                                a meia-noite como continuação da mesma jornada —
                                e não como um novo dia — e separa as horas
                                trabalhadas no período noturno para a apuração
                                do adicional noturno.
                            </IconeAjuda>
                        }
                    />
                </Grid>
                <Grid item xs={10}>
                    <FormFieldCheckbox
                        name="desconsiderarFeriados"
                        label="Desconsiderar feriados"
                        endAdornment={
                            <IconeAjuda>
                                Marque quando o funcionário deve trabalhar
                                normalmente nos feriados.
                                <br />
                                <br />
                                Os feriados da cidade de trabalho deixam de ser
                                aplicados a ele: o dia é apurado como dia comum,
                                sem folga prevista e sem as horas extras de
                                feriado.
                            </IconeAjuda>
                        }
                    />
                </Grid>
            </Grid>
            <div className="h-16" />
        </section>
    )
}
